import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import { episodesByDrama } from '@/mocks/episodes';
import ShareButton from '@/components/feature/ShareButton';
import Seo from '@/components/feature/Seo';

type FontSize = 'sm' | 'base' | 'lg';
type ColorMode = 'light' | 'night';

const fontClass: Record<FontSize, string> = {
  sm: 'text-sm leading-loose',
  base: 'text-base leading-loose',
  lg: 'text-lg leading-loose',
};

export default function EpisodeDetail() {
  const { id, episodeId } = useParams<{ id: string; episodeId: string }>();
  const [fontSize, setFontSize] = useState<FontSize>('base');
  const [colorMode, setColorMode] = useState<ColorMode>('light');

  const drama = dramas.find((item) => item.id === id) ?? null;
  const episodes = id ? episodesByDrama[id] ?? [] : [];
  const currentNumber = Number(episodeId);
  const episode = episodes.find((item) => item.number === currentNumber) ?? null;

  const prevEpisode = episodes.find((item) => item.number === currentNumber - 1) ?? null;
  const nextEpisode = episodes.find((item) => item.number === currentNumber + 1) ?? null;

  if (!drama) {
    return (
      <div className="px-4 md:px-6 lg:px-10 py-24 flex flex-col items-center text-center">
        <Seo title="剧集不存在 - 上司的老婆" description="未找到对应的电视剧内容。" />
        <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
          <i className="ri-film-line"></i>
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-900">没有找到这部剧</h1>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回电视剧分类
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Seo
        title={episode ? `${drama.title} 第${currentNumber}集 ${episode.title} - 分集剧情` : `${drama.title} 第${currentNumber}集 - 分集剧情`}
        description={`${drama.title}第${currentNumber}集剧情介绍与分集梗概，${drama.genres.join('、')}题材，${drama.actors.join('、')}主演。`}
        keywords={`${drama.title},第${currentNumber}集,分集剧情,剧情介绍`}
      />

      <div className="px-4 md:px-6 lg:px-10 pt-6 md:pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/category" className="hover:text-primary-600 transition-colors cursor-pointer">电视剧分类</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/detail/${drama.id}`} className="hover:text-primary-600 transition-colors cursor-pointer line-clamp-1">{drama.title}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">第 {currentNumber} 集</span>
        </nav>
      </div>

      <section className="px-4 md:px-6 lg:px-10 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-10">
          <div>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs mb-3">
                  <i className="ri-book-open-line"></i>
                  分集剧情章节
                </span>
                <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                  {drama.title} · 第 {currentNumber} 集
                </h1>
                {episode ? (
                  <p className="text-base text-foreground-600 mt-2">{episode.title}</p>
                ) : null}
              </div>
              <Link
                to={`/detail/${drama.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-list-unordered"></i>
                返回剧集总目录
              </Link>
            </div>

            {episode ? (
              <article
                className={`rounded-lg border p-6 md:p-8 transition-colors ${
                  colorMode === 'night'
                    ? 'bg-foreground-950 border-foreground-800 text-background-100'
                    : 'bg-background-50 border-background-200 text-foreground-800'
                }`}
              >
                <h2 className={`font-heading text-lg md:text-xl font-bold mb-5 ${colorMode === 'night' ? 'text-background-50' : 'text-foreground-950'}`}>
                  第 {currentNumber} 集 · {episode.title}
                </h2>
                <p className={`${fontClass[fontSize]} ${colorMode === 'night' ? 'text-background-200' : 'text-foreground-700'}`}>
                  {episode.content}
                </p>
                <p className={`${fontClass[fontSize]} mt-5 ${colorMode === 'night' ? 'text-background-200' : 'text-foreground-700'}`}>
                  本集通过{episode.title}这一关键节点，进一步推进了主线叙事，也为后续剧情埋下了新的伏笔。
                  想要了解完整的角色关系与故事走向，可以继续阅读下一集，或回到剧集总目录查看全部集数。
                </p>
              </article>
            ) : (
              <div
                className={`rounded-lg border p-10 flex flex-col items-center text-center ${
                  colorMode === 'night' ? 'bg-foreground-950 border-foreground-800' : 'bg-background-50 border-background-200'
                }`}
              >
                <span className="w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-2xl mb-4">
                  <i className="ri-time-line"></i>
                </span>
                <h2 className={`font-heading text-lg font-bold ${colorMode === 'night' ? 'text-background-50' : 'text-foreground-900'}`}>
                  该集内容暂未更新
                </h2>
                <p className={`text-sm mt-2 max-w-md ${colorMode === 'night' ? 'text-background-300' : 'text-foreground-500'}`}>
                  第 {currentNumber} 集的剧情章节尚未上线，你可以先阅读已更新的集数，或返回总目录查看其他内容。
                </p>
                <Link
                  to={`/detail/${drama.id}`}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回剧集总目录
                </Link>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 mt-6">
              {prevEpisode ? (
                <Link
                  to={`/detail/${drama.id}/episode/${prevEpisode.number}`}
                  className="flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-400 transition-colors cursor-pointer min-w-0"
                >
                  <i className="ri-arrow-left-s-line text-lg text-primary-500"></i>
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-400">上一集</span>
                    <span className="block text-sm text-foreground-800 line-clamp-1">{prevEpisode.title}</span>
                  </span>
                </Link>
              ) : (
                <span className="flex-1 px-4 py-3 rounded-lg border border-dashed border-background-300 text-xs text-foreground-400">
                  已经是第一集
                </span>
              )}
              {nextEpisode ? (
                <Link
                  to={`/detail/${drama.id}/episode/${nextEpisode.number}`}
                  className="flex-1 flex items-center justify-end gap-2 px-4 py-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-400 transition-colors cursor-pointer text-right min-w-0"
                >
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-400">下一集</span>
                    <span className="block text-sm text-foreground-800 line-clamp-1">{nextEpisode.title}</span>
                  </span>
                  <i className="ri-arrow-right-s-line text-lg text-primary-500"></i>
                </Link>
              ) : (
                <span className="flex-1 px-4 py-3 rounded-lg border border-dashed border-background-300 text-xs text-foreground-400 text-right">
                  已经是最后一集
                </span>
              )}
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-font-size-2 text-primary-500"></i>
                阅读设置
              </h2>
              <div className="mb-5">
                <span className="text-xs text-foreground-500 block mb-2">正文字号</span>
                <div className="flex items-center gap-2">
                  {(['sm', 'base', 'lg'] as FontSize[]).map((size, index) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => setFontSize(size)}
                      className={`flex-1 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                        fontSize === size ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                      }`}
                    >
                      {['小', '中', '大'][index]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs text-foreground-500 block mb-2">阅读模式</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setColorMode('light')}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                      colorMode === 'light' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                    }`}
                  >
                    <i className="ri-sun-line"></i>
                    日间
                  </button>
                  <button
                    type="button"
                    onClick={() => setColorMode('night')}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                      colorMode === 'night' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                    }`}
                  >
                    <i className="ri-moon-line"></i>
                    夜间
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-list-unordered text-accent-500"></i>
                本剧章节
              </h2>
              <ul className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                {episodes.map((item) => (
                  <li key={item.episodeId}>
                    <Link
                      to={`/detail/${drama.id}/episode/${item.number}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                        item.number === currentNumber
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <span className="w-12 shrink-0 text-xs text-foreground-400">第{item.number}集</span>
                      <span className="line-clamp-1">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <ShareButton title={`${drama.title} 第${currentNumber}集 ${episode ? episode.title : ''}`} className="justify-center" />
          </aside>
        </div>
      </section>
    </div>
  );
}